import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Key } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const SettingsModal = ({ open, onClose, user, darkMode = true }) => {
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const email = user?.email || "—";
  const provider = user?.auth_provider || (user?.picture ? "google" : null);

  useEffect(() => {
    if (!open) {
      setPasswordMessage("");
      setPasswordError("");
      return;
    }
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handlePasswordAction = async () => {
    if (!email || email === "—") return;
    setLoadingPassword(true);
    setPasswordMessage("");
    setPasswordError("");

    try {
      const params = new URLSearchParams();
      params.append("email", email);

      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: "POST",
        body: params,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      const data = await response.json();
      if (response.ok) {
        setPasswordMessage("A secure link has been sent to your email.");
      } else {
        setPasswordError(data.detail || "Failed to process request.");
      }
    } catch (err) {
      setPasswordError("Unable to connect to server.");
    } finally {
      setLoadingPassword(false);
    }
  };

  return (
    <>
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              key="settings-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-[120] flex items-center justify-center bg-ink-950/80 p-4 backdrop-blur-sm"
              onClick={onClose}
            >
              <motion.div
                key="settings-panel"
                role="dialog"
                aria-modal="true"
                initial={{ opacity: 0, y: 16, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.97 }}
                transition={{ type: "spring", stiffness: 300, damping: 26 }}
                onClick={(e) => e.stopPropagation()}
                className={`relative w-full max-w-md overflow-hidden rounded-3xl shadow-2xl ${darkMode ? "glass-strong" : "bg-white border border-slate-200"
                  }`}
                style={darkMode ? { background: "#0A1226" } : undefined}
              >
                <button
                  onClick={onClose}
                  className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${darkMode
                      ? "text-slate-500 hover:bg-white/[0.06] hover:text-slate-200"
                      : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    }`}
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>

                <div className="px-6 pb-2 pt-7">
                  <h3 className={`text-lg font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>
                    Settings
                  </h3>
                </div>

                {/* Password Management */}
                <div className="px-6 pb-2">
                  <p
                    className={`mb-2 text-[11px] font-semibold uppercase tracking-wider ${darkMode ? "text-slate-400" : "text-slate-500"
                      }`}
                  >
                    Account Security
                  </p>
                  <div
                    className={`rounded-2xl border p-4 ${darkMode ? "border-white/[0.06] bg-white/[0.02]" : "border-slate-200 bg-slate-50"
                      }`}
                  >
                    <button
                      onClick={handlePasswordAction}
                      disabled={loadingPassword}
                      className={`flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${darkMode
                          ? "border-sky-500/30 text-sky-300 hover:bg-sky-500/10"
                          : "border-sky-300 text-sky-600 hover:bg-sky-100"
                        }`}
                    >
                      <Key className="h-4 w-4" />
                      {loadingPassword ? "Sending..." : (provider === "google" ? "Create Password" : "Reset Password")}
                    </button>
                    {passwordMessage && (
                      <p className="mt-3 text-center text-xs font-medium text-green-500">{passwordMessage}</p>
                    )}
                    {passwordError && (
                      <p className="mt-3 text-center text-xs font-medium text-red-500">{passwordError}</p>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end px-6 py-5">
                  <button
                    onClick={onClose}
                    className="rounded-xl bg-accent-gradient px-4 py-2 text-sm font-semibold text-[#04121F] shadow-glow-sm transition-all hover:brightness-110"
                  >
                    Done
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}


    </>
  );
};

export default SettingsModal;
